import { NodeProps, useReactFlow } from '@xyflow/react';
import { RefreshCw, Link, Filter, Settings } from 'lucide-react';
import React, { useState, useEffect, useRef, memo } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import useDataSettings from '@/hooks/useDataSettings';
import NodeBaseContainer from '../base/base-container/NodeBaseContainer';
import NodeConfigurationDialog from '../../dialogs/configuration-dialog/NodeConfigurationDialog';
import NodeDataSettingsDialog from '../../dialogs/data-settings-dialog/NodeDataSettingsDialog';
import NodeReferenceSelectorDialog from '../../dialogs/reference-selector-dialog/NodeReferenceSelectorDialog';

interface NodeGlobalConstantsData {
  label?: string;
  manualConstants?: string;
  linkedConstants?: string;
  setReferences?: Record<string, string[]>;
  context?: string;
  linkedKnowledge?: string;
  isMinimized?: boolean;
  isLocked?: boolean;
  assignedEmails?: string[] | string;
  configuration?: {
    globalconstants?: boolean;
    [key: string]: any;
  };
  dataSettings?: any;
}

// Utility function to handle scrolling
function handleScroll(element: HTMLElement, deltaY: number) {
  if (!element) return false;
  const { scrollTop, scrollHeight, clientHeight } = element;
  const isAtTop = scrollTop <= 0;
  const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 2;
  const isScrollingUp = deltaY < 0;
  const isScrollingDown = deltaY > 0;

  if ((isScrollingUp && !isAtTop) || (isScrollingDown && !isAtBottom)) {
    element.scrollTop += deltaY;
    return true; // Event was handled
  }
  return false; // Let it bubble for panning
}

const NodeGlobalConstants: React.FC<NodeProps & { data: NodeGlobalConstantsData }> = (props) => {
  const { id, data } = props;
  const { updateNodeData } = useReactFlow();

  // Initialize state from props.data
  const [manualConstants, setManualConstants] = useState(data.manualConstants || '');
  const [localManualConstants, setLocalManualConstants] = useState('');
  const [linkedConstants, setLinkedConstants] = useState(data.linkedConstants || '');
  const [isLocked, setIsLocked] = useState(data.isLocked || false);
  const [isMinimized, setIsMinimized] = useState(data.isMinimized || false);
  const [assignedEmails, setAssignedEmails] = useState<string[]>(
    Array.isArray(data.assignedEmails) ? data.assignedEmails :
      (typeof data.assignedEmails === 'string' ? data.assignedEmails.trim().split(/\s+/).filter(Boolean) : [])
  );
  const [activeTab, setActiveTab] = useState<'manual' | 'linked'>('manual');
  const [minimizeLevel, setMinimizeLevel] = useState<'normal' | 'medium' | 'minimum'>('normal');

  // Refs for scroll handling
  const manualTextareaRef = useRef<HTMLTextAreaElement>(null);
  const linkedTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync local state when props.data changes
  useEffect(() => {
    if (data.manualConstants !== undefined && data.manualConstants !== manualConstants) {
      setManualConstants(data.manualConstants);
    }
    if (data.linkedConstants !== undefined && data.linkedConstants !== linkedConstants) {
      setLinkedConstants(data.linkedConstants);
    }
    if (data.isMinimized !== undefined) setIsMinimized(data.isMinimized);
    if (data.isLocked !== undefined) setIsLocked(data.isLocked);    if (data.assignedEmails !== undefined) {
      const emails = Array.isArray(data.assignedEmails) ? data.assignedEmails :
        (typeof data.assignedEmails === 'string' ? data.assignedEmails.trim().split(/\s+/).filter(Boolean) : []);
      setAssignedEmails(emails);
    }
  }, [data]);

  // Initialize minimize level from data
  useEffect(() => {
    if (data.minimizeLevel === undefined) {
      updateNodeData(id, { ...data, minimizeLevel: 'normal' });
    } else {
      setMinimizeLevel(data.minimizeLevel);
      setIsMinimized(data.minimizeLevel !== 'normal');
    }
  }, [data.minimizeLevel, id, data, updateNodeData]);

  // Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {};

    if (data.constants && Object.keys(data.constants).length > 0) {
      minifiedInfo.constants = `${Object.keys(data.constants).length} constant${Object.keys(data.constants).length !== 1 ? 's' : ''}`;
    }

    if (data.scope) {
      minifiedInfo.scope = data.scope;
    }

    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {};
    const hasChanges = JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo);

    if (hasChanges) {
      updateNodeData(id, { ...data, minifiedInfo });
    }
  }, [data.constants, data.scope, data.minifiedInfo, id, data, updateNodeData]);

  // Helper to sync node data
  const syncNodeData = (newData: Partial<NodeGlobalConstantsData>) => {
    const updatedData = { ...data, ...newData };
    updateNodeData(id, updatedData);
  };

  // --- Set References functionality ---
  const [isReferenceDialogVisible, setIsReferenceDialogVisible] = useState(false);
  const [availableNodes, setAvailableNodes] = useState<any[]>([]);

  useEffect(() => {
    setLinkedConstants(data.linkedConstants)
  }, [data.linkedConstants])

  useEffect(() => {
    setLocalManualConstants(data.manualConstants)
  }, [data.manualConstants])

  // Function to compute available nodes dynamically from flow
  const computeAvailableNodes = () => {
    const allNodes = (window as any).__flowGetAllNodes?.() || [];
    return allNodes
      .filter((n: any) => n.id !== id && n.data)
      .map((n: any) => {
        const dataPoints: { id: string; label: string; description?: string }[] = [];

        // Add full document if exists
        if (n.data?.dataSettings?.documentSections?.fullDocument) {
          dataPoints.push({ id: 'fullDocument', label: 'Full Document' });
        }        // Add all sections if exist
        const sections = n.data?.dataSettings?.documentSections?.sections || {};
        Object.entries(sections).forEach(([title]: [string, any]) => {
          dataPoints.push({ id: `section:${title}`, label: title });
        });

        // Add all exposed data points if exist
        const exposedData = n.data?.dataSettings?.exposedData || {};
        Object.entries(exposedData).forEach(([key, field]: [string, any]) => {
          dataPoints.push({
            id: `exposed:${key}`,
            label: field.label || key,
            description: field.description || '',
          });
        });

        return {
          id: n.id,
          label: n.data?.label || n.id,
          dataPoints,
        };
      });
  };

  // Update availableNodes when dialog is opened
  const handleOpenReferenceDialog = () => {
    setAvailableNodes(computeAvailableNodes());
    setIsReferenceDialogVisible(true);
  };

  // Save references handler: store in node data and update linked constants
  const handleSaveReferences = (selected: Record<string, string[]>, isLoading = false) => {
    const allNodes = (window as any).__flowGetAllNodes?.() || [];

    let combined = '';

    Object.entries(selected).forEach(([nodeId, dataPointIds]) => {
      const node = allNodes.find((n: any) => n.id === nodeId);
      if (!node) return;

      dataPointIds.forEach((dpId) => {
        let content = '';
        let sectionHeader = dpId;

        if (dpId === 'fullDocument') {
          content = node.data?.contentMarkdown || node.data?.content || '';
          sectionHeader = `${node.data?.label || nodeId} - Full Document`;
        } else if (dpId.startsWith('section:')) {
          const sectionTitle = dpId.replace('section:', '');
          const section = node.data?.dataSettings?.documentSections?.sections?.[sectionTitle];
          content = section?.value || '';
          sectionHeader = `${node.data?.label || nodeId} - ${sectionTitle}`;
        } else if (dpId.startsWith('exposed:')) {
          const key = dpId.replace('exposed:', '');
          const field = node.data?.dataSettings?.exposedData?.[key];
          content = field?.value || '';
          sectionHeader = `${node.data?.label || nodeId} - ${field?.label || key}`;
        }

        combined += `\\n::: section ${sectionHeader}\\n${content}\\n:::\\n`;
      });
    });

    //setLinkedConstants(combined);
    syncNodeData({
      linkedConstants: combined,
      ...(isLoading ? {} : { setReferences: selected }),
    });
  };

  // Helper to prepare exposedData fields for Global Constants
  const prepareExposedDataFields = () => ({
    manualConstants: {
      value: manualConstants,
      enabled: true,
      label: 'Manual Constants',
      description: 'Manually entered global constants for this project.',
      permissions: { readable: true, writable: false },
    },
    linkedConstants: {
      value: linkedConstants,
      enabled: true,
      label: 'Linked Constants',
      description: 'Constants built from references to other nodes.',
      permissions: { readable: true, writable: false },
    },
    allConstants: {
      value: `${manualConstants}\\n\\n${linkedConstants}`,
      enabled: true,
      label: 'All Constants',
      description: 'Combined manual and linked constants.',
      permissions: { readable: true, writable: false },
    },
  });

  // Keep exposedData in sync with constants
  useEffect(() => {
    const newExposed = prepareExposedDataFields();
    setDataSettings((prev) => ({
      ...prev,
      exposedData: newExposed,
    }));
    // Also sync to node data so other nodes see it
    syncNodeData({
      dataSettings: {
        ...(data.dataSettings || {}),
        exposedData: newExposed,
      }
    });
  // Only run when constants change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualConstants, linkedConstants]);

  // DataSettings and Configuration
  const [isConfigDialogVisible, setIsConfigDialogVisible] = useState(false);
  const [isDataSettingsDialogVisible, setIsDataSettingsDialogVisible] = useState(false);
  const [nodeConfig, setNodeConfig] = useState<any>(data.configuration || {});  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleRemoveInputLink,
    handleSyncInputLink,
    handleOpenDataSettingsDialog,
  } = useDataSettings(
    id,
    data,
    syncNodeData,
    false,
    '',
    new Map(),
    '',
    setIsDataSettingsDialogVisible
  );

  // Refresh imported data uses handleSaveReferences
  const handleRefreshLinkedConstants = () => {
    handleSaveReferences(data.setReferences || {}, true);
  };

  // Compute status for NodeBaseContainer using useMemo
  const nodeStatus = React.useMemo(() => {
    if (data.statusMessage?.toLowerCase().includes("error")) return "error";
    if (manualConstants || linkedConstants) return "success";
    return "initial";
  }, [data.statusMessage, manualConstants, linkedConstants]);

  // Custom buttons for NodeBaseContainer menu
  const customButtons = [
    {
      id: 'set-references',
      onClick: handleOpenReferenceDialog,
      icon: <Link size={16} />,
      tooltip: 'Set references for linked constants',
    },
    {
      id: 'data-settings',
      onClick: handleOpenDataSettingsDialog,
      icon: <Filter size={16} />,
      tooltip: 'Data connection settings',
    },
    {
      id: 'refresh-linked-constants',
      onClick: handleRefreshLinkedConstants,
      icon: <RefreshCw size={16} />,
      tooltip: 'Refresh linked constants',
    },
    {
      id: 'configuration',
      onClick: () => setIsConfigDialogVisible(true),
      icon: <Settings size={16} />,
      tooltip: 'Configuration',
    },
  ];

  const handleMinimizeLevelChange = (level: 'normal' | 'medium' | 'minimum') => {
    setMinimizeLevel(level)
    setIsMinimized(level !== 'normal')
    updateNodeData(id, {
      ...data,
      minimizeLevel: level,
      isMinimized: level !== 'normal'
    })
  }

  // Attach wheel event for textarea scroll containers
  useEffect(() => {
    const manualContainer = manualTextareaRef.current;
    const linkedContainer = linkedTextareaRef.current;

    const handleWheelEvent = (e: WheelEvent) => {
      const target = e.target as HTMLElement;
      if (handleScroll(target, e.deltaY)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    if (manualContainer) {
      manualContainer.addEventListener('wheel', handleWheelEvent, { passive: false });
    }
    if (linkedContainer) {
      linkedContainer.addEventListener('wheel', handleWheelEvent, { passive: false });
    }

    return () => {
      if (manualContainer) {
        manualContainer.removeEventListener('wheel', handleWheelEvent);
      }
      if (linkedContainer) {
        linkedContainer.removeEventListener('wheel', handleWheelEvent);
      }
    };
  }, []);

  return (
    <>
      <NodeBaseContainer
        id={id}
        data={data}
        resizable={!isLocked}
        assignedEmails={assignedEmails}
        nodeTitle={data.label || 'Global Constants'}
        customButtons={customButtons}
        status={nodeStatus}
        minimizeLevel={data.minimizeLevel || 'normal'}
        onMinimizeLevelChange={handleMinimizeLevelChange}
        onAssignUsersClick={() => {
          const emailsStr = window.prompt('Enter emails separated by space', Array.isArray(assignedEmails) ? assignedEmails.join(' ') : (assignedEmails || ''))
          if (emailsStr !== null) {
            const emails = emailsStr.trim().split(/\\s+/).filter(Boolean);
            setAssignedEmails(emails);
            syncNodeData({ assignedEmails: emails });
          }
        }}
      >
        {(data.minimizeLevel || 'normal') === 'normal' && (
          <div className="flex flex-col h-full">
            <Tabs value={activeTab} onValueChange={setActiveTab as any} className="flex flex-col h-full w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="manual">Manual Constants</TabsTrigger>
                <TabsTrigger value="linked">Linked Constants</TabsTrigger>
              </TabsList>

              <TabsContent value="manual" className="flex-1 overflow-hidden">
                <div className="flex flex-col h-full space-y-2">
                  <div className="flex-grow relative min-h-0 w-full">
                    <textarea
                      ref={manualTextareaRef}
                      className="w-full h-full absolute inset-0 p-2 border rounded nodrag resize-none"
                      placeholder="Enter global constants manually. These will be available to all nodes in the project.\\n\\nExample:\\nPROJECT_NAME=My Project\\nAPI_VERSION=v1.0\\nDEFAULT_THEME=dark"
                      value={localManualConstants}
                      onChange={(e) => {
                        setLocalManualConstants(e.target.value);
                      }}
                      onBlur={(e) => {
                        syncNodeData({ manualConstants: e.target.value });
                      }}
                      disabled={isLocked}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="linked" className="flex-1 overflow-hidden">
                <div className="flex flex-col h-full space-y-2">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">
                      Constants built from linked nodes (read-only)
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleOpenReferenceDialog}
                      disabled={isLocked}
                    >
                      <Link size={14} className="mr-1" />
                      Set References
                    </Button>
                  </div>
                  <div className="flex-grow relative min-h-0 w-full">
                    <textarea
                      ref={linkedTextareaRef}
                      className="w-full h-full absolute inset-0 p-2 border rounded nodrag resize-none bg-gray-50"
                      placeholder="Linked constants will appear here when you set references to other nodes.\\n\\nUse 'Set References' button to select data from other nodes."
                      value={linkedConstants}
                      readOnly
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </NodeBaseContainer>

      {/* Set References Dialog */}
      {isReferenceDialogVisible && (
        <NodeReferenceSelectorDialog
          visible={isReferenceDialogVisible}
          onHide={() => setIsReferenceDialogVisible(false)}
          availableNodes={availableNodes}
          onSave={handleSaveReferences}
          initialSelected={data.setReferences}
        />
      )}

      {/* Data Settings Dialog */}
      {isDataSettingsDialogVisible && (
        <NodeDataSettingsDialog
          visible={isDataSettingsDialogVisible}
          onHide={() => setIsDataSettingsDialogVisible(false)}
          settings={dataSettings}
          nodeId={id}
          onSettingsChange={handleDataSettingsChange}
          onRemoveInputLink={handleRemoveInputLink}
          onSyncInputLink={handleSyncInputLink}
          forceShowAvailableNodes={true}
          debug={false}
        />
      )}

      {/* Configuration Dialog */}
      {isConfigDialogVisible && (
        <NodeConfigurationDialog
          visible={isConfigDialogVisible}
          onHide={() => setIsConfigDialogVisible(false)}
          configuration={nodeConfig}
          onSave={(config) => {
            setNodeConfig(config);
            syncNodeData({ configuration: config });
          }}
        />
      )}
    </>
  );
};

export default memo(NodeGlobalConstants);
